/**
 * Manage Auditors - Full Governance Page
 * SuperAdmin view: Create auditors, manage accreditations, upload CVs, assign to cert bodies.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../api/axiosClient';

// ── Constants ─────────────────────────────────────────────────────────────────

const AUDITOR_STATUS_FILTERS = ['all', 'active', 'suspended', 'revoked'];
const ACC_STATUS_FILTERS = ['all', 'pending', 'approved', 'suspended', 'revoked', 'expired'];
const ASSIGNMENT_TYPES = ['initial', 'surveillance', 'renewal', 'witness'];

const AUDITOR_STATUS_COLORS = {
  active: { background: '#d4edda', color: '#155724', border: '#c3e6cb' },
  suspended: { background: '#fff3cd', color: '#856404', border: '#ffeeba' },
  revoked: { background: '#f8d7da', color: '#721c24', border: '#f5c6cb' },
};

const ACC_STATUS_COLORS = {
  pending: { background: '#e9ecef', color: '#495057', border: '#dee2e6' },
  approved: { background: '#d4edda', color: '#155724', border: '#c3e6cb' },
  suspended: { background: '#fff3cd', color: '#856404', border: '#ffeeba' },
  revoked: { background: '#f8d7da', color: '#721c24', border: '#f5c6cb' },
  expired: { background: '#f1f1f1', color: '#6c757d', border: '#dee2e6' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function StatusBadge({ status, colorMap }) {
  const colors = colorMap[status] || { background: '#e9ecef', color: '#495057', border: '#dee2e6' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.4px',
      background: colors.background,
      color: colors.color,
      border: `1px solid ${colors.border}`,
    }}>
      {status || '—'}
    </span>
  );
}

function ActionBtn({ label, onClick, variant = 'default', small = false }) {
  const variants = {
    default: { background: '#6c757d', color: '#fff' },
    primary: { background: '#d4af37', color: '#1a1a1a' },
    success: { background: '#28a745', color: '#fff' },
    warning: { background: '#fd7e14', color: '#fff' },
    danger: { background: '#dc3545', color: '#fff' },
    info: { background: '#17a2b8', color: '#fff' },
  };
  const v = variants[variant] || variants.default;
  return (
    <button
      onClick={onClick}
      style={{
        padding: small ? '3px 8px' : '5px 12px',
        fontSize: small ? '0.72rem' : '0.8rem',
        fontWeight: '600',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        background: v.background,
        color: v.color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

// ── Auditor Form Fields (must be defined outside ManageAuditors to prevent remount on every keystroke) ──

function AuditorFormFields({ form, setForm, isEdit = false, s }) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div style={s.field}>
          <label style={s.label}>First Name *</label>
          <input style={s.input} value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
        </div>
        <div style={s.field}>
          <label style={s.label}>Last Name *</label>
          <input style={s.input} value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
        </div>
      </div>
      <div style={s.field}>
        <label style={s.label}>Email *</label>
        <input style={s.input} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
      </div>
      <div style={s.field}>
        <label style={s.label}>Phone</label>
        <input style={s.input} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
      </div>
      {isEdit && (
        <div style={s.field}>
          <label style={s.label}>Status</label>
          <select style={s.select} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="revoked">Revoked</option>
          </select>
        </div>
      )}
      <div style={s.field}>
        <label style={s.label}>Qualifications</label>
        <textarea style={s.textarea} value={form.qualifications} onChange={e => setForm(f => ({ ...f, qualifications: e.target.value }))} />
        <div style={s.hint}>One per line, e.g. "Lead Auditor ISO 9001"</div>
      </div>
      <div style={s.field}>
        <label style={s.label}>Specialisations</label>
        <textarea style={s.textarea} value={form.specialisations} onChange={e => setForm(f => ({ ...f, specialisations: e.target.value }))} />
        <div style={s.hint}>One per line, e.g. "ISO 9001"</div>
      </div>
      <div style={s.field}>
        <label style={s.label}>Regions</label>
        <textarea style={s.textarea} value={form.regions} onChange={e => setForm(f => ({ ...f, regions: e.target.value }))} />
        <div style={s.hint}>One per line, e.g. "UK", "Europe"</div>
      </div>
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

function ManageAuditors() {
  // Auditor list state
  const [auditors, setAuditors] = useState([]);
  const [auditorFilter, setAuditorFilter] = useState('all');
  const [loadingAuditors, setLoadingAuditors] = useState(false);

  // Accreditation list state
  const [accreditations, setAccreditations] = useState([]);
  const [accFilter, setAccFilter] = useState('all');
  const [loadingAccs, setLoadingAccs] = useState(false);

  // Cert bodies (for assignment dropdown)
  const [certBodies, setCertBodies] = useState([]);

  // Error / success banners
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ── Modal state ────────────────────────────────────────────────────────────

  // Create Auditor modal
  const [showCreateAuditor, setShowCreateAuditor] = useState(false);
  const [createForm, setCreateForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    qualifications: '', specialisations: '', regions: '',
  });
  const [creating, setCreating] = useState(false);

  // Edit Auditor modal
  const [showEditAuditor, setShowEditAuditor] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    qualifications: '', specialisations: '', regions: '', status: 'active',
  });
  const [editing, setEditing] = useState(false);

  // CV Upload modal
  const [showCvUpload, setShowCvUpload] = useState(false);
  const [cvTarget, setCvTarget] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [uploadingCv, setUploadingCv] = useState(false);

  // Create Accreditation modal
  const [showCreateAcc, setShowCreateAcc] = useState(false);
  const [accCreateForm, setAccCreateForm] = useState({ auditor_id: '', notes: '' });
  const [creatingAcc, setCreatingAcc] = useState(false);

  // Accreditation action modal (approve/suspend/revoke/reinstate)
  const [showAccAction, setShowAccAction] = useState(false);
  const [accActionTarget, setAccActionTarget] = useState(null);
  const [accActionType, setAccActionType] = useState('');
  const [accActionNotes, setAccActionNotes] = useState('');
  const [performingAction, setPerformingAction] = useState(false);

  // Evidence upload modal
  const [showEvidenceUpload, setShowEvidenceUpload] = useState(false);
  const [evidenceTarget, setEvidenceTarget] = useState(null);
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);

  // Assignment modal
  const [showAssign, setShowAssign] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignForm, setAssignForm] = useState({
    cert_body_id: '', assignment_type: 'initial', scheduled_date: '', notes: '',
  });
  const [assigning, setAssigning] = useState(false);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchAuditors = useCallback(async () => {
    setLoadingAuditors(true);
    try {
      const params = auditorFilter !== 'all' ? { status: auditorFilter } : {};
      const res = await axiosClient.get('/auditors/', { params });
      setAuditors(res.data || []);
    } catch {
      setError('Failed to load auditors.');
    } finally {
      setLoadingAuditors(false);
    }
  }, [auditorFilter]);

  const fetchAccreditations = useCallback(async () => {
    setLoadingAccs(true);
    try {
      const params = accFilter !== 'all' ? { status: accFilter } : {};
      const res = await axiosClient.get('/auditor-accreditations/', { params });
      setAccreditations(res.data || []);
    } catch {
      setError('Failed to load accreditations.');
    } finally {
      setLoadingAccs(false);
    }
  }, [accFilter]);

  const fetchCertBodies = async () => {
    try {
      const res = await axiosClient.get('/cert-bodies/');
      setCertBodies(res.data || []);
    } catch {
      // Non-critical
    }
  };

  useEffect(() => { fetchAuditors(); }, [fetchAuditors]);
  useEffect(() => { fetchAccreditations(); }, [fetchAccreditations]);
  useEffect(() => { fetchCertBodies(); }, []);

  const flashSuccess = (msg) => {
    setSuccess(msg);
    setError(null);
    setTimeout(() => setSuccess(null), 4000);
  };

  // ── Create Auditor ─────────────────────────────────────────────────────────

  const handleCreateAuditor = async () => {
    if (!createForm.first_name.trim() || !createForm.last_name.trim() || !createForm.email.trim()) {
      setError('First name, last name, and email are required.');
      return;
    }
    setCreating(true);
    try {
      await axiosClient.post('/auditors/', {
        first_name: createForm.first_name.trim(),
        last_name: createForm.last_name.trim(),
        email: createForm.email.trim(),
        phone: createForm.phone.trim() || null,
        qualifications: createForm.qualifications.split('\n').map(s => s.trim()).filter(Boolean),
        specialisations: createForm.specialisations.split('\n').map(s => s.trim()).filter(Boolean),
        regions: createForm.regions.split('\n').map(s => s.trim()).filter(Boolean),
      });
      setShowCreateAuditor(false);
      setCreateForm({ first_name: '', last_name: '', email: '', phone: '', qualifications: '', specialisations: '', regions: '' });
      fetchAuditors();
      flashSuccess('Auditor created successfully.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create auditor.');
    } finally {
      setCreating(false);
    }
  };

  // ── Delete Auditor ─────────────────────────────────────────────────────────

  const handleDeleteAuditor = async (auditor) => {
    if (!window.confirm(`Delete auditor "${auditor.first_name} ${auditor.last_name}"? This cannot be undone.`)) return;
    try {
      await axiosClient.delete(`/auditors/${auditor.id}/`);
      fetchAuditors();
      flashSuccess('Auditor deleted.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete auditor.');
    }
  };

  // ── Edit Auditor ───────────────────────────────────────────────────────────

  const openEdit = (auditor) => {
    setEditTarget(auditor);
    setEditForm({
      first_name: auditor.first_name,
      last_name: auditor.last_name,
      email: auditor.email,
      phone: auditor.phone || '',
      qualifications: (auditor.qualifications || []).join('\n'),
      specialisations: (auditor.specialisations || []).join('\n'),
      regions: (auditor.regions || []).join('\n'),
      status: auditor.status,
    });
    setShowEditAuditor(true);
  };

  const handleEditAuditor = async () => {
    setEditing(true);
    try {
      await axiosClient.patch(`/auditors/${editTarget.id}`, {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim() || null,
        qualifications: editForm.qualifications.split('\n').map(s => s.trim()).filter(Boolean),
        specialisations: editForm.specialisations.split('\n').map(s => s.trim()).filter(Boolean),
        regions: editForm.regions.split('\n').map(s => s.trim()).filter(Boolean),
        status: editForm.status,
      });
      setShowEditAuditor(false);
      fetchAuditors();
      flashSuccess('Auditor updated successfully.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update auditor.');
    } finally {
      setEditing(false);
    }
  };

  // ── CV Upload ──────────────────────────────────────────────────────────────

  const handleCvUpload = async () => {
    if (!cvFile) { setError('Please select a file.'); return; }
    setUploadingCv(true);
    try {
      const fd = new FormData();
      fd.append('file', cvFile);
      await axiosClient.post(`/auditors/${cvTarget.id}/upload-cv`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setShowCvUpload(false);
      setCvFile(null);
      fetchAuditors();
      flashSuccess('CV uploaded successfully.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload CV.');
    } finally {
      setUploadingCv(false);
    }
  };

  // ── Create Accreditation ───────────────────────────────────────────────────

  const handleCreateAcc = async () => {
    if (!accCreateForm.auditor_id) { setError('Please select an auditor.'); return; }
    setCreatingAcc(true);
    try {
      await axiosClient.post('/auditor-accreditations/', {
        auditor_id: parseInt(accCreateForm.auditor_id),
        notes: accCreateForm.notes.trim() || null,
      });
      setShowCreateAcc(false);
      setAccCreateForm({ auditor_id: '', notes: '' });
      fetchAccreditations();
      flashSuccess('Accreditation application created.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create accreditation.');
    } finally {
      setCreatingAcc(false);
    }
  };

  // ── Accreditation Actions ──────────────────────────────────────────────────

  const openAccAction = (acc, actionType) => {
    setAccActionTarget(acc);
    setAccActionType(actionType);
    setAccActionNotes('');
    setShowAccAction(true);
  };

  const handleAccAction = async () => {
    setPerformingAction(true);
    try {
      await axiosClient.patch(
        `/auditor-accreditations/${accActionTarget.id}/${accActionType}`,
        { notes: accActionNotes.trim() || null }
      );
      setShowAccAction(false);
      fetchAccreditations();
      fetchAuditors(); // refresh accreditation_status on auditors
      flashSuccess(`Accreditation ${accActionType}d successfully.`);
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${accActionType} accreditation.`);
    } finally {
      setPerformingAction(false);
    }
  };

  // ── Evidence Upload ────────────────────────────────────────────────────────

  const handleEvidenceUpload = async () => {
    if (!evidenceFile) { setError('Please select a file.'); return; }
    setUploadingEvidence(true);
    try {
      const fd = new FormData();
      fd.append('file', evidenceFile);
      await axiosClient.post(`/auditor-accreditations/${evidenceTarget.id}/upload-evidence`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setShowEvidenceUpload(false);
      setEvidenceFile(null);
      fetchAccreditations();
      flashSuccess('Evidence uploaded successfully.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload evidence.');
    } finally {
      setUploadingEvidence(false);
    }
  };

  // ── Assignment ─────────────────────────────────────────────────────────────

  const handleAssign = async () => {
    if (!assignForm.cert_body_id) { setError('Please select a certification body.'); return; }
    setAssigning(true);
    try {
      await axiosClient.post(`/auditors/${assignTarget.id}/assign`, {
        cert_body_id: parseInt(assignForm.cert_body_id),
        assignment_type: assignForm.assignment_type,
        scheduled_date: assignForm.scheduled_date || null,
        notes: assignForm.notes.trim() || null,
      });
      setShowAssign(false);
      setAssignForm({ cert_body_id: '', assignment_type: 'initial', scheduled_date: '', notes: '' });
      fetchAuditors();
      flashSuccess('Auditor assigned successfully.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create assignment.');
    } finally {
      setAssigning(false);
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────

  const s = {
    container: { padding: '24px', maxWidth: '1400px', margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' },
    title: { fontSize: '1.9rem', fontWeight: '700', color: '#1a1a1a', margin: 0 },
    subtitle: { color: '#666', margin: '6px 0 0 0', fontSize: '0.95rem' },
    banner: (type) => ({
      padding: '10px 16px',
      borderRadius: '6px',
      marginBottom: '18px',
      fontWeight: '500',
      fontSize: '0.9rem',
      background: type === 'error' ? '#ffebee' : '#e8f5e9',
      border: `1px solid ${type === 'error' ? '#ef9a9a' : '#a5d6a7'}`,
      color: type === 'error' ? '#b71c1c' : '#1b5e20',
    }),
    sectionCard: {
      background: '#fff',
      border: '2px solid #d4af37',
      borderRadius: '8px',
      marginBottom: '32px',
      overflow: 'hidden',
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 20px',
      background: '#1a1a1a',
      color: '#d4af37',
    },
    sectionTitle: { fontSize: '1rem', fontWeight: '700', margin: 0 },
    filterRow: {
      display: 'flex',
      gap: '8px',
      padding: '12px 20px',
      background: '#f9f5e7',
      borderBottom: '1px solid #e8ddb5',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    pill: (active) => ({
      padding: '4px 14px',
      borderRadius: '16px',
      fontSize: '0.8rem',
      fontWeight: '600',
      cursor: 'pointer',
      border: '2px solid',
      borderColor: active ? '#d4af37' : '#ccc',
      background: active ? '#d4af37' : '#fff',
      color: active ? '#1a1a1a' : '#666',
      textTransform: 'capitalize',
    }),
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
      padding: '10px 14px',
      textAlign: 'left',
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      fontWeight: '700',
      color: '#666',
      borderBottom: '2px solid #e8ddb5',
      whiteSpace: 'nowrap',
    },
    td: {
      padding: '10px 14px',
      fontSize: '0.87rem',
      color: '#333',
      borderBottom: '1px solid #f0f0f0',
      verticalAlign: 'middle',
    },
    actionGroup: { display: 'flex', gap: '5px', flexWrap: 'wrap' },
    btnPrimary: {
      padding: '8px 18px',
      background: '#d4af37',
      color: '#1a1a1a',
      border: 'none',
      borderRadius: '6px',
      fontWeight: '700',
      fontSize: '0.9rem',
      cursor: 'pointer',
    },
    emptyRow: { padding: '32px', textAlign: 'center', color: '#999', fontSize: '0.95rem' },
    overlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    modal: {
      background: '#fff',
      borderRadius: '10px',
      padding: '28px',
      width: '100%',
      maxWidth: '520px',
      maxHeight: '85vh',
      overflowY: 'auto',
      boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
    },
    modalTitle: { fontSize: '1.2rem', fontWeight: '700', color: '#1a1a1a', marginBottom: '20px' },
    field: { marginBottom: '14px' },
    label: { display: 'block', fontWeight: '600', fontSize: '0.85rem', color: '#555', marginBottom: '5px' },
    input: {
      width: '100%', padding: '8px 10px', fontSize: '0.9rem',
      border: '1.5px solid #ccc', borderRadius: '5px', boxSizing: 'border-box',
    },
    textarea: {
      width: '100%', padding: '8px 10px', fontSize: '0.85rem',
      border: '1.5px solid #ccc', borderRadius: '5px', boxSizing: 'border-box',
      minHeight: '80px', resize: 'vertical',
    },
    select: {
      width: '100%', padding: '8px 10px', fontSize: '0.9rem',
      border: '1.5px solid #ccc', borderRadius: '5px', boxSizing: 'border-box',
      background: '#fff',
    },
    modalFooter: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' },
    btnCancel: {
      padding: '8px 18px', background: '#f1f1f1', border: '1px solid #ccc',
      borderRadius: '6px', fontWeight: '600', cursor: 'pointer',
    },
    btnSubmit: {
      padding: '8px 18px', background: '#d4af37', color: '#1a1a1a',
      border: 'none', borderRadius: '6px', fontWeight: '700', cursor: 'pointer',
    },
    hint: { fontSize: '0.78rem', color: '#888', marginTop: '3px' },
  };

  // ── Renderers ──────────────────────────────────────────────────────────────

  function renderAuditorsTable() {
    const filtered = auditorFilter === 'all' ? auditors : auditors.filter(a => a.status === auditorFilter);
    return (
      <div style={s.sectionCard}>
        <div style={s.sectionHeader}>
          <h2 style={s.sectionTitle}>Auditors ({filtered.length})</h2>
          <button style={s.btnPrimary} onClick={() => setShowCreateAuditor(true)}>+ Add Auditor</button>
        </div>
        <div style={s.filterRow}>
          <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#888' }}>Status:</span>
          {AUDITOR_STATUS_FILTERS.map(f => (
            <button key={f} style={s.pill(auditorFilter === f)} onClick={() => setAuditorFilter(f)}>
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
        <div className="ds-table-scroll" style={{ overflowX: 'auto' }}>
          {loadingAuditors ? (
            <div style={s.emptyRow}>Loading auditors…</div>
          ) : filtered.length === 0 ? (
            <div style={s.emptyRow}>No auditors found. Add one using the button above.</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  {['ID', 'Name', 'Email', 'Phone', 'Specialisations', 'Status', 'Accreditation', 'Acc. Expires', 'CV', 'Actions'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => (
                  <tr key={a.id}>
                    <td style={s.td}>{a.id}</td>
                    <td style={{ ...s.td, fontWeight: '600' }}>{a.full_name}</td>
                    <td style={s.td}>{a.email}</td>
                    <td style={s.td}>{a.phone || '—'}</td>
                    <td style={s.td}>
                      {(a.specialisations || []).length > 0
                        ? a.specialisations.join(', ')
                        : <span style={{ color: '#bbb' }}>—</span>}
                    </td>
                    <td style={s.td}>
                      <StatusBadge status={a.status} colorMap={AUDITOR_STATUS_COLORS} />
                    </td>
                    <td style={s.td}>
                      {a.accreditation_status
                        ? <StatusBadge status={a.accreditation_status} colorMap={ACC_STATUS_COLORS} />
                        : <span style={{ color: '#bbb' }}>None</span>}
                    </td>
                    <td style={s.td}>
                      {a.accreditation_expires_at
                        ? new Date(a.accreditation_expires_at).toLocaleDateString()
                        : <span style={{ color: '#bbb' }}>—</span>}
                    </td>
                    <td style={s.td}>
                      {a.cv_url
                        ? <a href={a.cv_url} target="_blank" rel="noreferrer" style={{ color: '#d4af37', fontWeight: '600' }}>View CV</a>
                        : <span style={{ color: '#bbb' }}>None</span>}
                    </td>
                    <td style={s.td}>
                      <div style={s.actionGroup}>
                        <ActionBtn label="Edit" variant="default" small onClick={() => openEdit(a)} />
                        <ActionBtn label="Upload CV" variant="info" small onClick={() => { setCvTarget(a); setShowCvUpload(true); }} />
                        <ActionBtn label="Assign" variant="primary" small onClick={() => { setAssignTarget(a); setShowAssign(true); }} />
                        <ActionBtn label="Delete" variant="danger" small onClick={() => handleDeleteAuditor(a)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  function renderAccreditationsTable() {
    return (
      <div style={s.sectionCard}>
        <div style={s.sectionHeader}>
          <h2 style={s.sectionTitle}>Auditor Accreditations ({accreditations.length})</h2>
          <button style={s.btnPrimary} onClick={() => setShowCreateAcc(true)}>+ New Application</button>
        </div>
        <div style={s.filterRow}>
          <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#888' }}>Status:</span>
          {ACC_STATUS_FILTERS.map(f => (
            <button key={f} style={s.pill(accFilter === f)} onClick={() => setAccFilter(f)}>
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
        <div className="ds-table-scroll" style={{ overflowX: 'auto' }}>
          {loadingAccs ? (
            <div style={s.emptyRow}>Loading accreditations…</div>
          ) : accreditations.length === 0 ? (
            <div style={s.emptyRow}>No accreditations found for the selected filter.</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  {['ID', 'Auditor', 'Email', 'Status', 'Issued', 'Expires', 'Evidence', 'Notes', 'Actions'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {accreditations.map(acc => {
                  const status = acc.status;
                  return (
                    <tr key={acc.id}>
                      <td style={s.td}>{acc.id}</td>
                      <td style={{ ...s.td, fontWeight: '600' }}>{acc.auditor_name || `Auditor #${acc.auditor_id}`}</td>
                      <td style={s.td}>{acc.auditor_email || '—'}</td>
                      <td style={s.td}>
                        <StatusBadge status={status} colorMap={ACC_STATUS_COLORS} />
                      </td>
                      <td style={s.td}>
                        {acc.issued_at ? new Date(acc.issued_at).toLocaleDateString() : '—'}
                      </td>
                      <td style={s.td}>
                        {acc.expires_at
                          ? <span style={{ color: new Date(acc.expires_at) < new Date() ? '#dc3545' : 'inherit' }}>
                              {new Date(acc.expires_at).toLocaleDateString()}
                            </span>
                          : '—'}
                      </td>
                      <td style={s.td}>
                        {acc.evidence_url
                          ? <a href={acc.evidence_url} target="_blank" rel="noreferrer" style={{ color: '#d4af37', fontWeight: '600' }}>View</a>
                          : <span style={{ color: '#bbb' }}>None</span>}
                      </td>
                      <td style={{ ...s.td, maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {acc.notes || '—'}
                      </td>
                      <td style={s.td}>
                        <div style={s.actionGroup}>
                          {(status === 'pending') && (
                            <>
                              <ActionBtn label="Approve" variant="success" small onClick={() => openAccAction(acc, 'approve')} />
                              <ActionBtn label="Revoke" variant="danger" small onClick={() => openAccAction(acc, 'revoke')} />
                            </>
                          )}
                          {status === 'approved' && (
                            <>
                              <ActionBtn label="Suspend" variant="warning" small onClick={() => openAccAction(acc, 'suspend')} />
                              <ActionBtn label="Revoke" variant="danger" small onClick={() => openAccAction(acc, 'revoke')} />
                            </>
                          )}
                          {status === 'suspended' && (
                            <>
                              <ActionBtn label="Reinstate" variant="success" small onClick={() => openAccAction(acc, 'reinstate')} />
                              <ActionBtn label="Revoke" variant="danger" small onClick={() => openAccAction(acc, 'revoke')} />
                            </>
                          )}
                          {(status === 'revoked' || status === 'expired') && (
                            <ActionBtn label="Reinstate" variant="success" small onClick={() => openAccAction(acc, 'reinstate')} />
                          )}
                          <ActionBtn label="Evidence" variant="info" small onClick={() => { setEvidenceTarget(acc); setShowEvidenceUpload(true); }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  // ── Modals ─────────────────────────────────────────────────────────────────

  const ACTION_LABELS = {
    approve: { label: 'Approve Accreditation', variant: 'success', desc: 'This will set the accreditation to Approved with a 36-month validity period.' },
    suspend: { label: 'Suspend Accreditation', variant: 'warning', desc: 'This will temporarily suspend the auditor\'s accreditation.' },
    revoke: { label: 'Revoke Accreditation', variant: 'danger', desc: 'This will permanently revoke the accreditation. It can be reinstated later.' },
    reinstate: { label: 'Reinstate Accreditation', variant: 'success', desc: 'This will reinstate the accreditation with a fresh 36-month validity period.' },
  };

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Manage Auditors</h1>
          <p style={s.subtitle}>Create and manage ICSC-accredited auditors, their accreditations, and assignments.</p>
        </div>
      </div>

      {/* Banners */}
      {error && <div style={s.banner('error')}>{error} <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '700' }}>✕</button></div>}
      {success && <div style={s.banner('success')}>{success}</div>}

      {/* Auditors Table */}
      {renderAuditorsTable()}

      {/* Accreditations Table */}
      {renderAccreditationsTable()}

      {/* ── Create Auditor Modal ── */}
      {showCreateAuditor && (
        <div style={s.overlay} onClick={() => setShowCreateAuditor(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Add New Auditor</div>
            <AuditorFormFields form={createForm} setForm={setCreateForm} s={s} />
            <div style={s.modalFooter}>
              <button style={s.btnCancel} onClick={() => setShowCreateAuditor(false)}>Cancel</button>
              <button style={s.btnSubmit} onClick={handleCreateAuditor} disabled={creating}>
                {creating ? 'Creating…' : 'Create Auditor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Auditor Modal ── */}
      {showEditAuditor && editTarget && (
        <div style={s.overlay} onClick={() => setShowEditAuditor(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Edit Auditor — {editTarget.full_name}</div>
            <AuditorFormFields form={editForm} setForm={setEditForm} isEdit s={s} />
            <div style={s.modalFooter}>
              <button style={s.btnCancel} onClick={() => setShowEditAuditor(false)}>Cancel</button>
              <button style={s.btnSubmit} onClick={handleEditAuditor} disabled={editing}>
                {editing ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CV Upload Modal ── */}
      {showCvUpload && cvTarget && (
        <div style={s.overlay} onClick={() => setShowCvUpload(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Upload CV — {cvTarget.full_name}</div>
            <div style={s.field}>
              <label style={s.label}>Select CV File (PDF, DOC, DOCX — max 10 MB)</label>
              <input type="file" accept=".pdf,.doc,.docx" onChange={e => setCvFile(e.target.files[0])} />
            </div>
            {cvTarget.cv_url && (
              <p style={{ fontSize: '0.82rem', color: '#666' }}>
                Current: <a href={cvTarget.cv_url} target="_blank" rel="noreferrer" style={{ color: '#d4af37' }}>View existing CV</a>
              </p>
            )}
            <div style={s.modalFooter}>
              <button style={s.btnCancel} onClick={() => { setShowCvUpload(false); setCvFile(null); }}>Cancel</button>
              <button style={s.btnSubmit} onClick={handleCvUpload} disabled={uploadingCv || !cvFile}>
                {uploadingCv ? 'Uploading…' : 'Upload CV'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Accreditation Modal ── */}
      {showCreateAcc && (
        <div style={s.overlay} onClick={() => setShowCreateAcc(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>New Accreditation Application</div>
            <div style={s.field}>
              <label style={s.label}>Auditor *</label>
              <select style={s.select} value={accCreateForm.auditor_id} onChange={e => setAccCreateForm(f => ({ ...f, auditor_id: e.target.value }))}>
                <option value="">— Select Auditor —</option>
                {auditors.map(a => (
                  <option key={a.id} value={a.id}>{a.full_name} ({a.email})</option>
                ))}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>Notes</label>
              <textarea style={s.textarea} placeholder="Optional notes about this application…"
                value={accCreateForm.notes} onChange={e => setAccCreateForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnCancel} onClick={() => setShowCreateAcc(false)}>Cancel</button>
              <button style={s.btnSubmit} onClick={handleCreateAcc} disabled={creatingAcc}>
                {creatingAcc ? 'Creating…' : 'Create Application'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Accreditation Action Modal ── */}
      {showAccAction && accActionTarget && (() => {
        const cfg = ACTION_LABELS[accActionType] || {};
        return (
          <div style={s.overlay} onClick={() => setShowAccAction(false)}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
              <div style={s.modalTitle}>{cfg.label}</div>
              <p style={{ fontSize: '0.87rem', color: '#555', marginBottom: '16px' }}>
                {cfg.desc}
              </p>
              <p style={{ fontSize: '0.85rem', color: '#444', marginBottom: '16px' }}>
                <strong>Auditor:</strong> {accActionTarget.auditor_name || `#${accActionTarget.auditor_id}`}
              </p>
              <div style={s.field}>
                <label style={s.label}>Notes (optional)</label>
                <textarea style={s.textarea} placeholder="Reason for this action…"
                  value={accActionNotes} onChange={e => setAccActionNotes(e.target.value)} />
              </div>
              <div style={s.modalFooter}>
                <button style={s.btnCancel} onClick={() => setShowAccAction(false)}>Cancel</button>
                <button
                  style={{ ...s.btnSubmit, background: accActionType === 'approve' || accActionType === 'reinstate' ? '#28a745' : accActionType === 'suspend' ? '#fd7e14' : '#dc3545', color: '#fff' }}
                  onClick={handleAccAction} disabled={performingAction}
                >
                  {performingAction ? 'Processing…' : cfg.label}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Evidence Upload Modal ── */}
      {showEvidenceUpload && evidenceTarget && (
        <div style={s.overlay} onClick={() => setShowEvidenceUpload(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Upload Evidence — Accreditation #{evidenceTarget.id}</div>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '12px' }}>
              Auditor: <strong>{evidenceTarget.auditor_name}</strong>
            </p>
            <div style={s.field}>
              <label style={s.label}>Select Evidence File (PDF, PNG, JPG, DOC/DOCX — max 10 MB)</label>
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={e => setEvidenceFile(e.target.files[0])} />
            </div>
            {evidenceTarget.evidence_url && (
              <p style={{ fontSize: '0.82rem', color: '#666' }}>
                Current: <a href={evidenceTarget.evidence_url} target="_blank" rel="noreferrer" style={{ color: '#d4af37' }}>View existing evidence</a>
              </p>
            )}
            <div style={s.modalFooter}>
              <button style={s.btnCancel} onClick={() => { setShowEvidenceUpload(false); setEvidenceFile(null); }}>Cancel</button>
              <button style={s.btnSubmit} onClick={handleEvidenceUpload} disabled={uploadingEvidence || !evidenceFile}>
                {uploadingEvidence ? 'Uploading…' : 'Upload Evidence'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Assignment Modal ── */}
      {showAssign && assignTarget && (
        <div style={s.overlay} onClick={() => setShowAssign(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Assign Auditor — {assignTarget.full_name}</div>
            <div style={s.field}>
              <label style={s.label}>Certification Body *</label>
              <select style={s.select} value={assignForm.cert_body_id} onChange={e => setAssignForm(f => ({ ...f, cert_body_id: e.target.value }))}>
                <option value="">— Select Certification Body —</option>
                {certBodies.map(cb => (
                  <option key={cb.id} value={cb.id}>{cb.name}</option>
                ))}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>Assignment Type *</label>
              <select style={s.select} value={assignForm.assignment_type} onChange={e => setAssignForm(f => ({ ...f, assignment_type: e.target.value }))}>
                {ASSIGNMENT_TYPES.map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>Scheduled Date</label>
              <input type="date" style={s.input} value={assignForm.scheduled_date} onChange={e => setAssignForm(f => ({ ...f, scheduled_date: e.target.value }))} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Notes</label>
              <textarea style={s.textarea} value={assignForm.notes} onChange={e => setAssignForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnCancel} onClick={() => setShowAssign(false)}>Cancel</button>
              <button style={s.btnSubmit} onClick={handleAssign} disabled={assigning}>
                {assigning ? 'Assigning…' : 'Create Assignment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageAuditors;
